'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx/lite';
import { useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import type { AnyObject, ObjectSchema } from 'yup';
import * as yup from 'yup';
import type {
  ConsentField,
  FormField,
  GfFieldChoice,
  GfFieldWithChoices,
  GfFieldWithRulesSetting,
} from '~/types/graphql';
import { sha256 } from '~/utils/sha256';
import { GFDropdown } from './CustomDropdown';

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const newsletterSchema = yup.object().shape({
  input_4: yup.lazy((value) => {
    if (value !== undefined && value !== '') {
      return yup
        .string()
        .matches(phoneRegExp, 'Phone number is not valid')
        .optional()
        .nullable();
    }
    return yup.string().nullable().optional();
  }),
  input_5: yup.lazy((value) => {
    if (value !== undefined && value !== '') {
      return yup
        .string()
        .matches(/^[0-9]+$/, 'Must be only digits')
        .min(5, 'Must be exactly 5 digits')
        .max(5, 'Must be exactly 5 digits')
        .optional()
        .nullable();
    }
    return yup.string().nullable().optional();
  }),
  input_7: yup.lazy((value) => {
    if (value !== undefined && value !== '') {
      return yup
        .string()
        .matches(/^[0-9]+$/, 'Must be only digits')
        .min(5, 'Must be exactly 5 digits')
        .max(5, 'Must be exactly 5 digits')
        .optional()
        .nullable();
    }
    return yup.string().nullable().optional();
  }),
});

export function Form({
  formId,
  children,
  className,
  radius,
  team,
  bgColor = 'white',
  textColor = 'black',
  labelColor = 'black',
  confirmationMessage,
  submitText,
  gtmEventName,
}: {
  formId?: string;
  children?: React.ReactNode;
  className?: string;
  radius?: string;
  team?: string;
  bgColor?: string;
  textColor?: string;
  labelColor?: string;
  confirmationMessage?: string | null;
  submitText: string;
  gtmEventName: string;
}) {
  const methods = useForm({
    ...(gtmEventName === 'stay_updated_form'
      ? { resolver: yupResolver(newsletterSchema as ObjectSchema<AnyObject>) }
      : {}),
  });

  const [status, setStatus] = useState<
    'initial' | 'loading' | 'success' | 'error'
  >('initial');

  const base = process.env.NEXT_PUBLIC_WORDPRESS_URL;
  const subsitePath = team && team !== 'main' ? `${team}/` : '';
  const path = `${subsitePath}wp-json/gf/v2/forms/${formId}/submissions`;

  const onSubmit = async (data: Record<string, unknown>) => {
    setStatus('loading');

    const formData = { ...data };

    // flatten checkboxes
    Object.keys(formData).forEach((key) => {
      if (Array.isArray(formData[key])) {
        formData[key] = formData[key].forEach((value, i) => {
          formData[`${key}.${i}`] = value;
        });
        delete formData[key];
      }
    });

    // Format phone number to E.164 format if present
    if (formData.input_4) {
      const phoneNumber = formData.input_4 as string;
      // Remove all non-digit characters
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      // Add country code if not present (assuming US numbers)
      const e164Number = digitsOnly.startsWith('1')
        ? `+${digitsOnly}`
        : `+1${digitsOnly}`;
      formData.input_4 = e164Number;
    }

    try {
      const response = await fetch(`${base}/${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const json = await response.json();

      if (typeof json.is_valid === 'boolean') {
        // response is valid
        if (json.is_valid) {
          setStatus('success');

          // Push Newsletter form
          if (gtmEventName === 'stay_updated_form') {
            const [first, ...last] = `${formData.input_3}`.split(' ');
            window.dataLayer?.push({
              event: gtmEventName,
              sha256_phone_number: await sha256(`${formData.input_4}`),
              sha256_email_address: await sha256(`${formData.input_3}`),
              address: {
                sha256_first_name: await sha256(first),
                sha256_last_name: await sha256(last.join(' ')),
                postal_code: formData.input_7,
                region: formData.input_8,
              },
            });
          }

          if (gtmEventName === 'contact_form') {
            window.dataLayer?.push({
              event: gtmEventName,
              sha256_email_address: await sha256(`${formData.input_5}`),
            });
          }

          // push to datalayer
        } else {
          // handle with input errors
          setStatus('initial');
          // set error messages
          Object.keys(json.validation_messages).forEach((key) => {
            methods.setError(
              `input_${key}` as keyof typeof newsletterSchema.fields,
              {
                message: json.validation_messages[key],
              },
            );
          });
        }
      } else {
        // server error
        setStatus('error');
      }
    } catch (error) {
      console.log(error);
      setStatus('error');
    }
  };

  const statusMessage = {
    initial: '',
    loading: undefined, // show in button
    success: confirmationMessage,
    error: 'Something went wrong',
  }[status];

  const style = {
    ...(textColor ? { '--text-color': textColor } : {}),
    ...(bgColor ? { '--bg-color': bgColor } : {}),
    ...(labelColor ? { '--alt-color': labelColor } : {}),
    ...(radius ? { '--radius': radius } : {}),
  } as React.CSSProperties;

  return (
    <FormProvider {...methods}>
      {statusMessage ? (
        <div className={clsx('text-[--text-color]', className)} style={style}>
          <p
            className="m-0 font-roboto text-[16px] leading-normal tracking-tight"
            dangerouslySetInnerHTML={{ __html: statusMessage }}
          />
        </div>
      ) : (
        <form
          id={`form_${formId}`}
          className={clsx('flex w-full flex-col', className)}
          style={style}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          {children}
          <SubmitButton isSubmitting={status === 'loading'} team={team}>
            {submitText}
          </SubmitButton>
        </form>
      )}
    </FormProvider>
  );
}

export function Field({
  formId,
  field,
  // placeholder,
  className,
}: {
  formId: string;
  field: FormField &
    GfFieldWithRulesSetting &
    GfFieldWithChoices &
    ConsentField;
  placeholder?: string;
  className?: string;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const { id, type, label, checkboxLabel, choices, isRequired } = field;

  const fieldName = `field_${formId}_${id}`;
  const inputName = `input_${id}`;
  const inputType = {
    text: 'text',
    email: 'email',
    phone: 'tel',
    address: 'text',
    textarea: 'textarea',
    select: 'select',
    consent: 'checkbox',
  }[type?.toLowerCase() ?? ''];
  const hasError = !!errors?.[inputName]?.message;
  const isTextarea = inputType === 'textarea';
  const isCheckbox = inputType === 'checkbox';
  const isSelect = inputType === 'select';

  return (
    <div id={fieldName} className={clsx('relative w-full', className)}>
      {!isCheckbox && (
        <label
          htmlFor={inputName}
          className={clsx(
            'absolute left-[15px] top-[-5px] z-1 flex h-[9px] items-center bg-[--bg-color] px-[5px] font-roboto text-[13px] font-medium uppercase leading-normal',
            hasError ? 'text-[#DB1500]' : 'text-[--alt-color]',
          )}
        >
          {label}
          {isRequired ? '*' : ''}
        </label>
      )}

      {isTextarea ? (
        <textarea
          id={inputName}
          {...register(inputName)}
          className={clsx(
            'h-[160px] w-full rounded-[--radius] border px-5 py-[25px] font-roboto text-[13px] font-normal leading-[19px]',
            hasError ? 'border-[#DB1500]' : 'border-[--text-color]',
          )}
          required={!!isRequired}
          // placeholder={placeholder || field.placeholder}
          aria-invalid={!!hasError}
          aria-required={!!isRequired}
        />
      ) : isCheckbox ? (
        <>
          <input
            type={inputType}
            id={`${inputName}`}
            {...register(`${inputName}.1`)}
            className="peer hidden"
            required={!!isRequired}
            // placeholder={placeholder || field.placeholder}
            aria-invalid={!!hasError}
            aria-required={!!isRequired}
          />
          <label
            htmlFor={`${inputName}`}
            className={clsx(
              'flex cursor-pointer items-center gap-x-4 text-[13px] leading-[19px] tracking-[1px] peer-checked:[&_div]:before:bg-[--text-color]',
              hasError ? 'text-[#DB1500]' : 'text-[--text-color]',
            )}
          >
            <div
              className={clsx(
                "relative aspect-square h-[14px] w-[14px] rounded-[--radius] border before:absolute before:inset-[2px] before:rounded-[--radius] before:content-['']",
                hasError ? 'border-[#DB1500]' : 'border-[black]',
              )}
            />
            <span
              className="[&_a]:underline [&_a]:underline-offset-[2px]"
              dangerouslySetInnerHTML={{
                __html: checkboxLabel + (isRequired ? '*' : ''),
              }}
            />
          </label>
        </>
      ) : isSelect ? (
        <CustomSelect
          inputName={inputName}
          hasError={hasError}
          isRequired={!!isRequired}
          choices={choices as GfFieldChoice[]}
        />
      ) : (
        <input
          type={inputType}
          id={inputName}
          {...register(inputName)}
          className={clsx(
            'h-[70px] w-full rounded-[--radius] border bg-[--bg-color] px-5 font-roboto text-[13px] font-normal leading-[19px] text-[--text-color]',
            hasError ? 'border-[#DB1500]' : 'border-[--text-Color]',
          )}
          required={!!isRequired}
          // placeholder={placeholder || field.placeholder}
          aria-invalid={!!hasError}
          aria-required={!!isRequired}
        />
      )}

      {errors?.[inputName]?.message && (
        <p className="m-0 w-full font-roboto text-[13px] font-normal text-[#DB1500]">
          {errors[inputName]?.message as string}
        </p>
      )}
    </div>
  );
}

function CustomSelect({
  inputName,
  hasError,
  isRequired,
  choices,
}: {
  inputName: string;
  hasError: boolean;
  isRequired: boolean;
  choices: GfFieldChoice[];
}) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={inputName}
      defaultValue={choices[0].value ?? ''}
      render={({ field }) => (
        <GFDropdown
          {...field}
          choices={choices}
          isRequired={isRequired}
          hasError={hasError}
        />
      )}
    />
  );
}

function SubmitButton({
  className,
  children,
  isSubmitting,
  team,
}: React.HTMLProps<HTMLButtonElement> & {
  isSubmitting?: boolean;
  team?: string;
}) {
  return (
    <button
      type="submit"
      className={clsx(
        'flex w-full items-center justify-center gap-6 rounded-[--radius] bg-[--theme-primary] p-6 font-roboto text-lg font-medium uppercase tracking-[.17em] max-lg:mt-4',
        team ? 'text-[--theme-action]' : 'text-black',
        isSubmitting && 'cursor-wait',
        className,
      )}
    >
      {isSubmitting ? 'Submitting' : children}
    </button>
  );
}
